import { Constants } from '@src/app/commons/Constants';
import { DEBUG } from '@src/DEBUG';
DEBUG.open ( '/xs/service/eventify' );

import * as _ from 'lodash'
import { Observable, of, ReplaySubject, Subject, Subscription } from 'rxjs';

/*
  Eventify a service / helper / controller / whatever
*/

export class XsEventify {

  debug:boolean = Constants.debug_off;
  debugStyle:string = 'color:#fff;background-color:#921;';
  parentClass:string = null;

  static NO_VALUE:string = 'no_value';
  static REGISTER_ONLY_ONE:boolean = true;
  static REGISTER_ALL:boolean = false;

  // our local subscriptions
  _subs:Subscription[] = [];

  // subjects for all our supported events
  __subjects = {};

  // sub-events
  __sub_event_keys = [ 'Init', 'Start', 'Process', 'During', 'End', 'Ready', 'Error' ];

  // subject timeout (repeat triggering) in milliseconds
  subjectRepeatTimeout:number = 2000;

  constructor ( keys:string[] = null, parentClass:string = null ) {

    DEBUG.log ( '/xs/service/eventify' );

    if ( parentClass ) {
      this.parentClass = parentClass;
    } else {
      this.parentClass = this.constructor.name;
    }

    this.__log ( 'CONSTRUCTOR', keys );

    if ( keys ) {
      this.__register_events(keys);
    }
  }

  __log ( funcy:string = '??', data:any = '' ) {
    if ( this.debug ) { 
      console.log ( '%c __REGISTRY %c '+funcy+' %c '+this.parentClass+' ', Constants._red, Constants._black, Constants._gray, data ); 
    }
  }

  // register what events this Eventified object supports
  __register_events( event_keys:string[] ) {
    event_keys.forEach ( i => {
      this.__register_event(i);
    });
  }
  __register_event ( key:string, onlyOne:boolean = false ) {
    if ( onlyOne ) {
      if ( this.__subjects[key] ) {
        // already created, possibly through when()
      } else {
        this.__subjects[key] = { s: new ReplaySubject<any>(1,this.subjectRepeatTimeout), o:null };
      }
      this.__log ( 'register_event('+key+') CREATE ONLY_ONE' );
    } else {
      let f = [];
      this.__sub_event_keys.forEach ( e => {
        if ( this.__subjects[key+e] ) {
          // already created, possibly through when()
        } else {
          this.__subjects[key+e] = { s: new ReplaySubject<any>(1,this.subjectRepeatTimeout), o:null };
        }
        
        f.push(key+e);
      });
      this.__log ( 'register_event('+key+') CREATE ALL', f );
    }
  }

  // call internal methods for where we are in the process
  _init( key:string, object:any = null ) { this._e( key, 'Init', object ); }
  _start( key:string, object:any = null ) { this._e( key, 'Start', object ); }
  _process( key:string, object:any = null ) { this._e( key, 'Process', object ); }
  _during( key:string, object:any = null ) { this._e( key, 'During', object ); }
  _end( key:string, object:any = null ) { this._e( key, 'End', object ); }
  _ready( key:string, object:any = null ) { this._e( key, 'Ready', object ); }
  _error( key:string, object:any = null ) { this._e( key, 'Error', object ); }
  _e( key:string, who:string = '', object:any = null, source:any = null ) {
    const from = this._from(source);
    this.__log ( 'trigger('+key+who+') from ['+from+']', [object, this.__subjects[key+who]] );
    if ( this.__subjects[key+who] ) {
      this.__subjects[key+who].s.next(object);
      // this.__subjects[key+who].s.finally(null);
    }
  }

  _from ( source:any = null ) {
    let from = 'unknown';
    if ( source ) {
      if ( source.__xs ) {
        from = source.id;
      } else
      if ( source.__ID ) {
        from = source.__ID.id;
      } else {
        if ( source.constructor ) {
          from = source.constructor.name;
        } else {
          from = typeof source;
        }
      }
    } 
    return from;
  }

  _isSubevent ( key:string ) {
    let ret = false;
    this.__sub_event_keys.forEach( e => { 
      if ( !ret ) { 
        ret = key.endsWith(e); 
      }
      // console.log('********',key,e, key.endsWith(e) );
    });
    return ret;
  }

  _register_external_event ( key:string, incoming:any = null, onlyOne:boolean = XsEventify.REGISTER_ALL, source:any = null ) {

    const from = this._from(source);
    
    // if registering an event, but there's no existing Subject; create one
    if ( !incoming ) {
      incoming = new ReplaySubject<any>(1,this.subjectRepeatTimeout);
    }

    // what is that incoming thing?
    let type:any = typeof incoming;
    if ( type == 'object' ) {
      type = incoming.constructor.name;
    }

    // if there's no registry entry, then create one
    if ( !this.__subjects[key] ) {
      this.__log ( 'register_external_event('+key+') from ['+from+'] CREATE NEW' );
      // if ( this.debug ) { console.log ( '%c '+this.parentClass+'(eventify) :: register_external_event('+key+') NEW', this.debugStyle, type, this.__subjects, incoming ); }
      this.__register_event ( key, onlyOne );
    }

    // existing entries are updates to the s: or o:
    if ( this.__subjects[key] && incoming ) {
      this.__log ( 'register_external_event('+key+') from ['+from+'] UPDATE', [ type, this.__subjects, incoming ] );
      // if ( this.debug ) { console.log ( '%c '+this.parentClass+'(eventify) :: register_external_event('+key+') EXISTING / UPDATE', this.debugStyle, type, this.__subjects, incoming ); }
      if ( type == 'Subject' ) {
        this.__subjects[key].s.subscribe(incoming);
      } else {
        // console.log('@@@@@ incoming event offers an Observable, but we prefer a Subject ...', source);
        this.__subjects[key].o = incoming;
      }
    }
  }

  // main way anyone subscribes to our events
  public when( key:string ) {

    if ( !this.__subjects[key] && !this.__subjects[key+'End'] ) {
      this.__log ( 'register_when('+key+') NOT FOUND; CREATING', this._isSubevent(key) );
      this.__register_event(key, this._isSubevent(key) );
    }
    if ( this.__subjects[key] ) {
      this.__log ( 'register_when('+key+')', this.__subjects[key] );
      // if ( this.debug ) { console.log ( '%c '+this.parentClass+'(eventify) :: when('+key+')', this.debugStyle, this.__subjects[key] ); }
      return this.__subjects[key].s;
    }

    if ( this.__subjects[key+'End'] ) {
      this.__log ( 'register_when('+key+') END', this.__subjects[key+'End'] );
      // if ( this.debug ) { console.log ( '%c '+this.parentClass+'(eventify) :: when('+key+') END', this.debugStyle, this.__subjects[key+'End'] ); }
      return this.__subjects[key+'End'].s;
    }

    // return this.__subjects[key].s;
    // if ( this.debug ) { console.log ( '%c '+this.parentClass+'(eventify) :: when('+key+') NOT_FOUND', this.debugStyle ); }
    return of(null);
  }

  // subscription management

  // subscribe to something
  subscribe ( f:any, key:string = 'n/a' ) {
    f['_key'] = key;
    this._subs.push(f);
  }

  // delete all of them
  unsubscribe ( key:string = null ) {
    if ( key ) {
      let find = this._subs.find ( item => item['_key'] ? item['_key'] == key : null );
      if ( find ) {
        find.unsubscribe();
        this._subs = this._subs.filter ( item => item['_key'] ? item['_key'] != key : true );
      }
    }
  }

  // delete all of them
  unsubscribeAll() {
    this._subs.forEach( item => item.unsubscribe() );
    this._subs = [];
  }

}
